import React from 'react'
import { Button, CircularProgress, Card, CardContent, Typography, Alert, AlertTitle, Chip, Box, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Divider, Paper } from '@mui/material'
import './dashboard.css'

import { Link, useLocation, useNavigate } from 'react-router-dom'
import BorderAllIcon from '@mui/icons-material/BorderAll';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import StoreIcon from '@mui/icons-material/Store';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GuideTour from '../../components/guide/GuideTour';
import { steps } from './guidSteps';
import { apiErrorManagemnt2 } from '../../api/Custom';
import Swal from 'sweetalert2';
import { savePaymentDetails } from '../recharge/payment';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUserDetails } from '../../store/userSlice';
import { useGetSellerDetailsQuery } from '../../hooks/RTK_Service/userLoginRegister';
import { useGetChannelOrdersQuery } from '../../hooks/RTK_Service/channelOrders';
import { useGetAllChannelsQuery } from '../../hooks/RTK_Service/channel';
import RefreshIcon from '@mui/icons-material/Refresh';

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [paymentLoadder, setPaymentLoadder] = useState(false);
  const [todayOrdersCount, setTodayOrdersCount] = useState(0);
  const [syncErrors, setSyncErrors] = useState([]);

  const userReq = useGetSellerDetailsQuery();
  const ordersReq = useGetChannelOrdersQuery({ page: 1, limit: 1000, channel: 'ALL', status: 'UNSHIPPED' }, {
    pollingInterval: 60000, // Refresh every 60 seconds
  });
  const channelsReq = useGetAllChannelsQuery({ page: 1, limit: 50 });

  if (userReq?.isError) {
    apiErrorManagemnt2(userReq?.error);
  }

  if (userReq?.isSuccess) {
    dispatch(setUserDetails({ user: userReq?.data }));
  }

  // Calculate today's NEW orders from channels (Amazon, Flipkart, etc.)
  useEffect(() => {
    if (ordersReq?.isSuccess && ordersReq?.data?.salesOrder?.content) {
      const orders = ordersReq.data.salesOrder.content;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const todayOrders = orders.filter(orderData => {
        const order = orderData[0]; // SalesOrder object
        const orderDate = new Date(order.createdDtTime);
        orderDate.setHours(0, 0, 0, 0);
        return orderDate.getTime() === today.getTime();
      });
      
      setTodayOrdersCount(todayOrders.length);
    }
  }, [ordersReq?.data]);

  // Check for sync errors
  useEffect(() => {
    const errors = [];
    
    if (channelsReq?.isSuccess && channelsReq?.data) {
      channelsReq.data.forEach(channel => {
        if (channel.orderSync === 'N') {
          errors.push({
            type: 'order',
            severity: 'warning',
            message: `${channel.channelName}: Order sync is OFF - New orders won't be imported`,
            channel: channel.channelName
          });
        }
        if (channel.inventorySync === 'N') {
          errors.push({
            type: 'inventory',
            severity: 'warning',
            message: `${channel.channelName}: Inventory sync is OFF - Stock won't be updated`,
            channel: channel.channelName
          });
        }
      });
    }
    
    setSyncErrors(errors);
  }, [channelsReq?.data]);

  // Check if user is a first-time user (no channels configured)
  const isFirstTimeUser = () => {
    return !channelsReq?.data || 
           channelsReq?.data?.length === 0;
  };


  const firstTimeUser = () => {
    if (userReq?.data?.user?.gstIn === null || userReq?.data?.user?.gstIn === "") {
      Swal.fire({
        title: "GST number not present. Please provide first.",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: "Provide Here",
      }).then((result) => {
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) {
          navigate("/dashboard/update_profile");
        }
      });
      return;
    }

    setPaymentLoadder(true)
    const data = {
      amt: 100,
      units: 200,
      billing_plan: 'FT',
    }

    savePaymentDetails(data).then(res => {
      setPaymentLoadder(false)
    })

  }
  
  return (
    <div className='container-fluid'>
      <div className='spaceBetween mb-3'>
        <div>
          <h3 className='text-muted mb-0'>Dashboard</h3>
          <small className='text-muted'>Welcome back! Here's what's happening today.</small>
        </div>
        <div className='alignCenter'>
          <GuideTour action={false} guideSteps={steps} />
          <RefreshIcon 
            className='pointer mx-2' 
            onClick={() => {
              userReq?.refetch();
              ordersReq?.refetch();
              channelsReq?.refetch();
            }} 
          />
        </div>
      </div>

      {/* First Time User Setup Alert */}
      {isFirstTimeUser() && (
        <Alert severity="info" className="mb-3" icon={<HelpOutlineIcon />}>
          <AlertTitle>👋 Welcome to ManageEcom!</AlertTitle>
          <Typography variant="body2" className="mb-2">
            Let's get you started with a quick setup. Follow these steps:
          </Typography>
          <Box className="mt-2">
            {(!userReq?.data?.user?.gstIn || userReq?.data?.user?.gstIn === "") && (
              <Chip 
                label="1. Add GST Number" 
                color="warning" 
                size="small" 
                className="m-1"
                onClick={() => navigate("/dashboard/update_profile")}
                clickable
              />
            )}
            {(!channelsReq?.data?.content || channelsReq?.data?.content?.length === 0) && (
              <Chip 
                label="2. Connect Your First Channel" 
                color="warning" 
                size="small" 
                className="m-1"
                onClick={() => navigate("/dashboard/add_channel")}
                clickable
              />
            )}
            {userReq?.data?.totalProductCount === 0 && (
              <Chip 
                label="3. Add Products" 
                color="warning" 
                size="small" 
                className="m-1"
                onClick={() => navigate("/dashboard/add_or_update_stock")}
                clickable
              />
            )}
          </Box>
        </Alert>
      )}

      {/* Sync Issues Alert */}
      {syncErrors.length > 0 && (
        <Alert severity="warning" className="mb-3" icon={<WarningIcon />}>
          <AlertTitle>⚠️ Channel Sync Issues Detected ({syncErrors.length})</AlertTitle>
          <Typography variant="body2" className="mb-2" style={{ fontWeight: 500 }}>
            Action Required: Enable synchronization for the following channels to ensure smooth operations:
          </Typography>
          <Box className="mt-2 mb-2" style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
            {syncErrors.map((error, index) => (
              <Typography key={index} variant="body2" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                <strong style={{ minWidth: '120px', color: '#856404' }}>
                  {error.type === 'order' ? '📦 Orders:' : '📊 Inventory:'}
                </strong>
                <span style={{ color: '#856404' }}>{error.message}</span>
              </Typography>
            ))}
          </Box>
          <Typography variant="body2" style={{ fontStyle: 'italic', marginBottom: '8px', color: '#666' }}>
            💡 To fix: Go to Channel Settings → Find the channel → Turn ON the sync toggle
          </Typography>
          <Button 
            size="small" 
            variant="contained"
            color="warning" 
            onClick={() => navigate("/dashboard/manage_channel")}
            className="mt-1"
            startIcon={<SettingsIcon />}
          >
            Open Channel Settings
          </Button>
        </Alert>
      )}

      {/* Main Stats Cards */}
      {/* Main Stats Cards */}
      <div className="row mb-4 mt-2">

        {/* NEW Orders Card */}
        <div className="col-12 col-sm-6 col-md-3 my-1" >
          <Link className='link' to={"<link>/dashboard/channel_orders</link>"}>
            <div className="order-tab card dashboard_card px-1 py-4" style={{ borderLeft: '8px solid #1976d2' }}>
              <div style={{ display: 'flex' }}>
                <i className='fa-solid fa-indian-rupee-sign center text-muted dash-icon-card'>
                  <BorderAllIcon style={{ fontSize: "50px" }} />
                </i>
                <div className="dash-text-card">
                  <h2 className='text-muted' ><b>NEW Orders</b></h2>
                  <h4 className='text-muted'>
                    {ordersReq?.isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      ordersReq?.data?.salesOrder?.content?.length || 0
                    )}
                  </h4>
                  <small className='text-muted'>Today's Total: {todayOrdersCount}</small>
                </div>
              </div>
            </div>
          </Link>
        </div>

        <div className="col-12 col-sm-6 col-md-3 my-1" >
          {/* onClick={() => { navigate("/dashboard/recharge_plans") }} */}
          {userReq?.data?.totalCountOfPayment === 0 ?
            <div onClick={() => { firstTimeUser() }} className="card dashboard_card px-1 py-4" style={{ borderLeft: '8px solid rgb(25 207 37)', cursor: "pointer" }}>
              <div style={{ display: 'flex' }}>
                <i className='fa-solid fa-indian-rupee-sign center text-success dash-icon-card'><CurrencyRupeeIcon style={{ fontSize: "50px" }} /></i>
                <div className="dash-text-card">
                  <h2 className='text-success' ><b>Recharge</b> {
                    paymentLoadder &&
                    <CircularProgress
                      variant="indeterminate"
                      disableShrink
                      size={25}
                      thickness={4}
                      color="inherit"
                    />} </h2>
                  <small className='text-muted' >click to get 200 points for 100/- recharge for first time user.</small>
                </div>
              </div>
            </div>
            :
            <div onClick={() => { navigate("/dashboard/recharge_plans") }} className="card dashboard_card px-1 py-4" style={{ borderLeft: '8px solid rgb(25 207 37)' }}>
              <div style={{ display: 'flex' }}>
                <i className='fa-solid fa-indian-rupee-sign center text-success dash-icon-card'><CurrencyRupeeIcon style={{ fontSize: "50px" }} /></i>
                <div className="dash-text-card">
                  <h2 className='text-success' ><b>Recharge</b></h2>
                  <h4 className='text-muted'>{userReq?.data?.user?.totalCreditsPoints}</h4>
                </div>
              </div>
            </div>
          }

        </div>

        <div className="col-12 col-sm-6 col-md-3 my-1" >
          <Link className='link' to={"/dashboard/manage_products"}>
            <div className="card dashboard_card px-1 py-4" style={{ borderLeft: '8px solid rgb(210 207 25)' }}>
              <div style={{ display: 'flex' }}>

                <i className='fa-solid fa-box center text-warning dash-icon-card'><Inventory2Icon style={{ fontSize: "50px" }} /></i>
                <div className="dash-text-card">
                  <h2 className='text-warning' ><b>Products</b></h2>
                  <h4 className='text-muted'>{userReq?.data?.totalProductCount}</h4>
                </div>
              </div>
            </div>
          </Link>

        </div>

        {/* Channels Card */}
        <div className="col-12 col-sm-6 col-md-3 my-1" >
          <Link className='link' to={"/dashboard/manage_channel"}>
            <div className="card dashboard_card px-1 py-4" style={{ borderLeft: '8px solid rgb(156, 39, 176)' }}>
              <div style={{ display: 'flex' }}>
                <i className='center text-purple dash-icon-card' style={{ color: 'rgb(156, 39, 176)' }}>
                  <StoreIcon style={{ fontSize: "50px" }} />
                </i>
                <div className="dash-text-card">
                  <h2 style={{ color: 'rgb(156, 39, 176)' }}><b>Channels</b></h2>
                  <h4 className='text-muted'>
                    {channelsReq?.isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      channelsReq?.data?.length || 0
                    )}
                  </h4>
                  {channelsReq?.data && channelsReq.data.length > 0 && (
                    <small className='text-muted'>
                      {channelsReq.data.filter(c => c.orderSync === 'Y').length} syncing
                    </small>
                  )}
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Sync Issues Card - Only show when there are errors */}
        {syncErrors.length > 0 && (
          <div className="col-12 col-sm-6 col-md-3 my-1" >
            <div 
              className="card dashboard_card px-1 py-4 pointer" 
              style={{ borderLeft: '8px solid #ff6b6b', cursor: 'pointer' }}
              onClick={() => navigate("/dashboard/manage_channel")}
            >
              <div style={{ display: 'flex' }}>
                <i className='center text-danger dash-icon-card'>
                  <WarningIcon style={{ fontSize: "50px" }} />
                </i>
                <div className="dash-text-card">
                  <h2 className='text-danger' ><b>Sync Issues</b></h2>
                  <h4 className='text-muted'>{syncErrors.length}</h4>
                  <small className='text-danger'>
                    {syncErrors.filter(e => e.type === 'order').length} order, {syncErrors.filter(e => e.type === 'inventory').length} stock
                  </small>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Quick Setup Guide & Status Section */}
      <div className="row mb-4">
        {/* Quick Setup Guide */}
        <div className="col-12 col-lg-6 mb-3">
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-3" gutterBottom>
                <PlaylistAddCheckIcon className="mr-2" /> Quick Setup Guide
              </Typography>
              <Divider className="mb-3" />
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    {userReq?.data?.user?.gstIn ? 
                      <CheckCircleIcon color="success" /> : 
                      <WarningIcon color="warning" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary="Step 1: Complete Your Profile"
                    secondary="Add GST number and business details"
                  />
                  {!userReq?.data?.user?.gstIn && (
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => navigate("/dashboard/update_profile")}
                    >
                      Setup
                    </Button>
                  )}
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {channelsReq?.data?.content && channelsReq.data.content.length > 0 ? 
                      <CheckCircleIcon color="success" /> : 
                      <WarningIcon color="warning" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary="Step 2: Connect Marketplace Channels"
                    secondary="Link Amazon, Flipkart, or other channels"
                  />
                  {(!channelsReq?.data?.content || channelsReq.data.content.length === 0) && (
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => navigate("/dashboard/add_channel")}
                    >
                      Add Channel
                    </Button>
                  )}
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {userReq?.data?.totalProductCount > 0 ? 
                      <CheckCircleIcon color="success" /> : 
                      <WarningIcon color="warning" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary="Step 3: Add Your Products"
                    secondary="Upload inventory and product details"
                  />
                  {userReq?.data?.totalProductCount === 0 && (
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => navigate("/dashboard/add_or_update_stock")}
                    >
                      Add Products
                    </Button>
                  )}
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {channelsReq?.data?.some(c => c.orderSync === 'Y') ? 
                      <CheckCircleIcon color="success" /> : 
                      <WarningIcon color="warning" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary="Step 4: Enable Order Sync"
                    secondary="Automatically sync orders from channels"
                  />
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => navigate("/dashboard/manage_channel")}
                  >
                    Configure
                  </Button>
                </ListItem>
              </List>

              <Divider className="my-3" />
              
              <Typography variant="body2" color="text.secondary" className="mt-2">
                💡 Need help? Check our video tutorials below or contact support.
              </Typography>
            </CardContent>
          </Card>
        </div>

        {/* Channel Status */}
        <div className="col-12 col-lg-6 mb-3">
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-3" gutterBottom>
                <SyncIcon className="mr-2" /> Channel Sync Status
              </Typography>
              <Divider className="mb-3" />
              
              {channelsReq?.isLoading ? (
                <Box className="text-center py-4">
                  <CircularProgress />
                </Box>
              ) : channelsReq?.data?.content && channelsReq.data.content.length > 0 ? (
                <List dense>
                  {channelsReq.data.content.map((channel) => (
                    <ListItem key={channel.channelId}>
                      <ListItemIcon>
                        {channel.orderSync === 'Y' && channel.inventorySync === 'Y' ? 
                          <CheckCircleIcon color="success" /> : 
                          <ErrorIcon color="error" />
                        }
                      </ListItemIcon>
                      <ListItemText 
                        primary={channel.channelName}
                        secondary={
                          <>
                            <Chip 
                              label={channel.orderSync === 'Y' ? 'Order Sync: ON' : 'Order Sync: OFF'} 
                              size="small" 
                              color={channel.orderSync === 'Y' ? 'success' : 'error'}
                              className="mr-1"
                            />
                            <Chip 
                              label={channel.inventorySync === 'Y' ? 'Inventory: ON' : 'Inventory: OFF'} 
                              size="small" 
                              color={channel.inventorySync === 'Y' ? 'success' : 'error'}
                            />
                          </>
                        }
                      />
                      <Button 
                        size="small" 
                        variant="text"
                        onClick={() => navigate("/dashboard/manage_channel")}
                      >
                        <SettingsIcon fontSize="small" />
                      </Button>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  <AlertTitle>No Channels Connected</AlertTitle>
                  Connect your first marketplace channel to start selling.
                  <Button 
                    size="small" 
                    color="info" 
                    onClick={() => navigate("/dashboard/add_channel")}
                    className="mt-2"
                  >
                    Add Channel Now
                  </Button>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Actions Section */}
      {/* Quick Actions Section */}
      <div className="row">
        <div className="col-sm-12 col-lg-6">
          <Card className="mb-3">
            <CardContent>
              <Typography variant="h6" className="mb-3" gutterBottom>
                ⚡ Quick Actions
              </Typography>
              <Divider className="mb-3" />
              
              <div className="row">
                <div className="col-12 col-sm-6 mb-2">
                  <Link to="/dashboard/add_or_update_stock" style={{ textDecoration: 'none' }}>
                    <Button
                      type="button"
                      fullWidth
                      variant="outlined"
                      sx={{ py: 2 }}
                      startIcon={<Inventory2Icon />}
                    >
                      Add Products
                    </Button>
                  </Link>
                </div>
                
                <div className="col-12 col-sm-6 mb-2">
                  <Link to="/dashboard/manage_channel" style={{ textDecoration: 'none' }}>
                    <Button
                      type="button"
                      fullWidth
                      variant="outlined"
                      sx={{ py: 2 }}
                      startIcon={<StoreIcon />}
                    >
                      Manage Channels
                    </Button>
                  </Link>
                </div>

                <div className="col-12 col-sm-6 mb-2">
                  <Link to="/dashboard/job_batch_management" style={{ textDecoration: 'none' }}>
                    <Button
                      type="button"
                      fullWidth
                      variant="outlined"
                      sx={{ py: 2 }}
                      startIcon={<PlaylistAddCheckIcon />}
                    >
                      Job/Batch Management
                    </Button>
                  </Link>
                </div>

                <div className="col-12 col-sm-6 mb-2">
                  <Link to="/dashboard/manage_stock" style={{ textDecoration: 'none' }}>
                    <Button
                      type="button"
                      fullWidth
                      variant="outlined"
                      sx={{ py: 2 }}
                      startIcon={<Inventory2Icon />}
                    >
                      Manage Inventory
                    </Button>
                  </Link>
                </div>

                <div className="col-12 col-sm-6 mb-2">
                  <Link to="/dashboard/recharge_plans" style={{ textDecoration: 'none' }}>
                    <Button
                      type="button"
                      fullWidth
                      variant="outlined"
                      color="success"
                      sx={{ py: 2 }}
                      startIcon={<CurrencyRupeeIcon />}
                    >
                      Recharge Account
                    </Button>
                  </Link>
                </div>

                <div className="col-12 col-sm-6 mb-2">
                  <Button
                    type="button"
                    fullWidth
                    variant="outlined"
                    sx={{ py: 2 }}
                    startIcon={<TrendingUpIcon />}
                    disabled
                  >
                    GST/Report (Coming Soon)
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="col-sm-12 col-lg-6">
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-3" gutterBottom>
                📚 Help & Resources
              </Typography>
              <Divider className="mb-3" />
              
              <Alert severity="info" className="mb-3">
                <AlertTitle>Self-Help Guide</AlertTitle>
                <Typography variant="body2">
                  Watch our step-by-step video tutorials to get started quickly:
                </Typography>
              </Alert>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <HelpOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="How to setup inventory management"
                    secondary="Learn to add and manage your products"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <HelpOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Connecting marketplace channels"
                    secondary="Link Amazon, Flipkart and sync orders"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <HelpOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Managing orders and fulfillment"
                    secondary="Process orders and track shipments"
                  />
                </ListItem>
              </List>

              <Box className="mt-3">
                <Typography variant="body2" color="text.secondary">
                  💬 Need more help? Contact our support team or check the documentation.
                </Typography>
              </Box>

              <Box className="mt-3">
                {/* Placeholder for video tutorials - commented out for now */}
                {/* <div className="mt-2">
                  <iframe 
                    width="100%" 
                    height="250" 
                    src="https://www.youtube.com/embed/YOUR_VIDEO_ID" 
                    frameBorder="0" 
                    allowFullScreen
                  ></iframe>
                </div> */}
                <Paper elevation={2} className="p-3 text-center">
                  <Typography variant="body2" color="text.secondary">
                    📹 Video tutorials coming soon!
                  </Typography>
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>

  )
}
